import { URL } from "../components/BaseUrl";

class MessagingApi {
    constructor(token) {
        this.token = token;
        this.baseUrl = `${URL}/api/messages`;
    }

    async _fetch(endpoint, options = {}) {
        const headers = {
            Authorization: `Bearer ${this.token}`,
            ...options.headers,
        };

        // Don't set Content-Type for FormData (browser sets it automatically with boundary)
        if (!(options.body instanceof FormData)) {
            headers["Content-Type"] = "application/json";
        }

        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers,
            });
            return await response.json();
        } catch (error) {
            console.error(`API Error [${endpoint}]:`, error);
            return { error: true, message: error.message };
        }
    }

    // 1. Get Chat Threads (Inbox)
    async getThreads(page = 1, limit = 20) {
        return this._fetch(`/threads?page=${page}&limit=${limit}`);
    }

    // 2. Get Eligible Chat Users - GET /messages/eligible-users?role=company_admin|worker|super_admin
    // Backend must filter by this role (not by JWT). Normalize UI role to API role.
    _normalizeRoleForEligibleUsers(role) {
        if (!role || typeof role !== "string") return "";
        const r = role.trim().toLowerCase();
        if (r === "superadmin") return "super_admin";
        if (r === "company_admin" || r === "worker") return r;
        return role;
    }

    async getEligibleUsers(search = "", page = 1, limit = 20, role = "") {
        const normalizedRole = this._normalizeRoleForEligibleUsers(role);
        const params = new URLSearchParams();
        if (normalizedRole) params.set("role", normalizedRole);
        params.set("search", search);
        params.set("page", String(page));
        params.set("limit", String(limit));
        const query = `?${params.toString()}`;
        return this._fetch(`/eligible-users${query}`);
    }

    // 3. Create or Get Thread
    async createThread(receiverId, receiverRole) {
        return this._fetch("/threads", {
            method: "POST",
            body: JSON.stringify({ receiverId, receiverRole }),
        });
    }

    // 4. Get Thread Messages
    async getThreadMessages(threadId, page = 1, limit = 20) {
        return this._fetch(`/threads/${threadId}/messages?page=${page}&limit=${limit}`);
    }

    // 5. Send Text Message
    async sendMessage(threadId, content, messageType = "text") {
        return this._fetch(`/threads/${threadId}/messages`, {
            method: "POST",
            body: JSON.stringify({ content, messageType }),
        });
    }

    // 6. Send Message with File
    async sendMessageWithFile(threadId, file, content = "", messageType = "image") {
        const formData = new FormData();
        formData.append("content", content);
        formData.append("messageType", messageType);
        formData.append("file", file);

        return this._fetch(`/threads/${threadId}/messages`, {
            method: "POST",
            body: formData,
        });
    }

    // 7. Mark Messages as Read
    async markAsRead(threadId, upToMessageId = null) {
        const body = upToMessageId ? { upToMessageId } : {};
        return this._fetch(`/threads/${threadId}/read`, {
            method: "PUT",
            body: JSON.stringify(body),
        });
    }

    // 8. Get Unread Counts
    async getUnreadCounts() {
        return this._fetch("/unread-counts/detailed");
    }
}

export default MessagingApi;
